import React, { useContext, useState } from 'react'
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { AccountGroupDto } from 'api/Trading-Accounts/Types/AccountGroupDto'
import { AccountGroup } from 'api/Trading-Accounts/Types/IntroducingBrokerDetailedProfileDto'
import AddButton from 'components/AddButton/AddButton'
import { Modal } from 'components/CustomModal'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { normalizeFilter } from 'features/DataGrid'
import { useApiClient } from 'utils/api/ApiClient'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import stringComparator from 'utils/stringComparator'

import SelectAccountGroupTable from '../../../../Records/AccountGroup/SelectAccountGroupTable'
import introducingBrokerProfileAccountGroupsColumns from './introducingBrokerProfileAccountGroupsColumns'
import { AccountGroupNameDto, IntroducingBrokerProfileProps } from './types'

export const IntroducingBrokerProfileAccountGroupsPage: React.FC<IntroducingBrokerProfileProps> = (
  props
) => {
  const { profile } = props

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<{ skip: number; take: number }>(
    getPaginationInitialState()
  )
  const { callback: fetchPaymentAgents, forceRefresh } = useCallbackWithForceRefresh(
    () => tradingAccountsApiClient.getIntroducingBrokerProfileAccountGroups(profile.id),
    [profile.id]
  )
  const { data, isLoading } = useFetchOne(fetchPaymentAgents)

  const [isAccountGroupOpen, setAccountGroupModalOpen] = useState<boolean>(false)
  const setToast = useContext(ToastContext)

  const handlePageChange = (event: GridPageChangeEvent) => {
    if (data && data.length >= 1) {
      setPaginationState({ skip: event.page.skip, take: event.page.take })
    }
  }

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(normalizeFilter(event.filter))
    setPaginationState(getPaginationInitialState())
  }

  const handleAccountGroupModalOpen = () => {
    setAccountGroupModalOpen(true)
  }

  const handleAccountGroupModalClose = () => {
    setAccountGroupModalOpen(false)
  }

  const handleSelectedAccountGroups = async (accountGroupDtos: AccountGroupDto[]) => {
    if (data) {
      const groups = accountGroupDtoToNameDto(accountGroupDtos).concat(dataToNameDto(data))
      await partnershipsApiClient.updateIntroducingBrokerProfileAccountGroups(profile.id, {
        accountGroups: groups.map((x) => x.id.toString()),
      })
      forceRefresh()
      setToast(successToast('Account Group updated successfully.'))
    }
  }

  const handleRemoveAccountGroup = async (accountGroupId: string) => {
    if (data && data.length >= 1) {
      await partnershipsApiClient.updateIntroducingBrokerProfileAccountGroups(profile.id, {
        accountGroups: data.filter((a) => a.id !== accountGroupId).map((x) => x.id),
      })
      forceRefresh()
      setToast(successToast('Account Group removed successfully.'))
    }
  }

  const gridData = filterBy(data || [], filter).sort((a: AccountGroup, b: AccountGroup) =>
    stringComparator(a.name, b.name)
  )

  return (
    <Loading isLoading={isLoading}>
      {isAccountGroupOpen && (
        <Modal visible={isAccountGroupOpen} onClose={handleAccountGroupModalClose}>
          <SelectAccountGroupTable
            introducingBrokerProfileId={profile.id}
            selectedAccountGroups={[]}
            onSelect={handleSelectedAccountGroups}
            onClose={handleAccountGroupModalClose}
          />
        </Modal>
      )}
      <AddButton text='Add' onClick={handleAccountGroupModalOpen} />
      <Grid
        resizable
        pageable
        filterable
        data={gridData.slice(paginationState.skip, paginationState.take + paginationState.skip)}
        filter={filter}
        skip={paginationState.skip}
        take={paginationState.take}
        total={gridData.length}
        onPageChange={handlePageChange}
        onFilterChange={handleFilterChange}
        data-test='table'
      >
        {introducingBrokerProfileAccountGroupsColumns
          .filter((c) => c.show !== false)
          .map((column, idx) => (
            <Column key={idx + 2} {...column} />
          ))}
        <Column
          title=''
          width={50}
          filterable={false}
          cell={(gridCellProps: GridCellProps) => (
            <td>
              <MoreButton
                items={[
                  {
                    name: 'Remove',
                    onClick: () => handleRemoveAccountGroup(gridCellProps.dataItem.id),
                  },
                ]}
              />
            </td>
          )}
        />
      </Grid>
    </Loading>
  )
}

const dataToNameDto = (accountGroups: AccountGroup[]): AccountGroupNameDto[] =>
  accountGroups.map((x) => ({
    id: x.id,
    name: x.name || '',
  }))

const accountGroupDtoToNameDto = (accountGroupDto: AccountGroupDto[]): AccountGroupNameDto[] =>
  accountGroupDto.map((x) => ({
    id: x.id,
    name: x.name,
  }))
